import React from "react";
import { observer } from "mobx-react";
import {
  Stack,
  Text,
  HStack,
  Box,
  Image,
  Button,
  Card,
  Table,
} from "@chakra-ui/react";
import { t } from "i18next";
import { DistanceUnit, getOptionTranslation } from "@keita/lanepatrol-sdk";

import { useStores } from "../../shared/stores/RootStore";
import { HlaScoreView } from "./HlaScoreView";
import { HlaLane } from "../models/HlaLane";
import { SimulationLane } from "../models/SimulationLane";
import { HlaWay } from "../models/HlaWay";
import { SimulationWay } from "../models/SimulationWay";
import { hlaAttributeOptions } from "../../map/models/HlaAttribute";
import { CrossSectionView } from "./HlaWayDetail/cross-section/CrossSectionView";
import { StreetViewComponent } from "./StreetView/StreetViewComponent";
import {
  ATTRIBUTE_LABELS,
  AttributeChange,
  Lane,
  LANE_FIELDS,
  WAY_FIELDS,
  WayLike,
} from "../models/ComparisonFields";

const isEqual = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

const humanizeKey = (key: string): string => {
  const withSpaces = key.replace(/([A-Z])/g, " $1").toLowerCase();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const attributeLabel = (key: string): string =>
  ATTRIBUTE_LABELS[key] ??
  t(`shared:hlaAttribute.${key}`, {
    defaultValue: humanizeKey(key),
  });

const getSpeedUnit = (distanceUnit: DistanceUnit): string =>
  distanceUnit === DistanceUnit.Imperial ? "mph" : "km/h";

const getOptionLabel = (value: unknown): string | null => {
  const optionGroups = Object.values(hlaAttributeOptions).filter(Boolean);

  for (const options of optionGroups) {
    const option = options.find((item) => item.value === value);
    if (option) return t(option.translation);
  }

  if (typeof value === "string") {
    const translation = getOptionTranslation(value as any);
    if (translation) return t(translation);
  }

  return null;
};

const formatScalar = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";

  return getOptionLabel(value) ?? String(value);
};

const formatNumber = (value: number): string => {
  return value.toLocaleString();
};

const getChangedObjectKeys = (value: unknown, other: unknown): string[] => {
  const valueObj = (value ?? {}) as Record<string, unknown>;
  const otherObj = (other ?? {}) as Record<string, unknown>;

  return Array.from(
    new Set([...Object.keys(valueObj), ...Object.keys(otherObj)]),
  ).filter((key) => !isEqual(valueObj[key], otherObj[key]));
};

const formatValue = (value: unknown, other: unknown): string => {
  if (value === null || value === undefined) return "No attribute selected";

  if (typeof value === "object" && !Array.isArray(value)) {
    const valueObj = value as Record<string, unknown>;
    const changedKeys = getChangedObjectKeys(value, other);

    if (changedKeys.length === 0) return "No change";

    return changedKeys
      .map((key) => `${attributeLabel(key)}: ${formatScalar(valueObj[key])}`)
      .join("\n");
  }

  return formatScalar(value);
};

const formatChangeValue = (
  changeKey: string,
  value: unknown,
  other: unknown,
  speedUnit: string,
): string => {
  const formattedValue = formatValue(value, other);

  if (
    changeKey === "averageRoadSpeed" &&
    value !== null &&
    value !== undefined
  ) {
    return `${formattedValue} ${speedUnit}`;
  }

  return formattedValue;
};

function diffFields<
  Before extends object,
  After extends object,
  Key extends keyof Before & keyof After,
>(
  before: Before | null | undefined,
  after: After | null | undefined,
  fields: { key: Key; label: string }[],
): AttributeChange[] {
  if (!before || !after) return [];

  return fields
    .filter(({ key }) => !isEqual(before[key], after[key]))
    .map(({ key, label }) => ({
      key: String(key),
      label,
      hlaValue: before[key],
      simulationValue: after[key],
    }));
}

const getWayChanges = (
  hlaWay?: HlaWay,
  simulationWay?: SimulationWay,
): AttributeChange[] => diffFields(hlaWay, simulationWay, WAY_FIELDS);

const getLaneChanges = (
  hlaLane?: HlaLane,
  simulationLane?: SimulationLane,
): AttributeChange[] => diffFields(hlaLane, simulationLane, LANE_FIELDS);

const PhotoBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    w="100%"
    h={{ "2xl": "400px", xl: "310px" }}
    aspectRatio={1}
    borderWidth="1px"
    borderRadius="md"
    overflow="hidden"
  >
    {children}
  </Box>
);

const ComparisonPhoto = ({
  photoUrl,
  alt,
  streetViewMapSource,
}: {
  photoUrl?: string | null;
  alt: string;
  streetViewMapSource?: React.ComponentProps<
    typeof StreetViewComponent
  >["streetViewMapSource"];
}) => (
  <PhotoBox>
    {photoUrl ? (
      <Image src={photoUrl} alt={alt} h="100%" w="100%" objectFit="cover" />
    ) : (
      <StreetViewComponent streetViewMapSource={streetViewMapSource} />
    )}
  </PhotoBox>
);

const ComparisonCard = ({
  title,
  photo,
  children,
}: {
  title: string;
  photo: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card.Root flexGrow={1}>
    <Stack gap={3} p={3}>
      <Text fontSize="xl" fontWeight="bold" textAlign="center">
        {title}
      </Text>

      <Stack gap={6} align="center" flexDir={{ "2xl": "row", xl: "column" }}>
        <Box w="full">{photo}</Box>
        <Box flex={2}>{children}</Box>
      </Stack>
    </Stack>
  </Card.Root>
);

const WayStats = ({
  averageRoadSpeed,
  averageDailyTraffic,
  type,
  speedUnit,
}: Pick<WayLike, "averageRoadSpeed" | "averageDailyTraffic" | "type"> & {
  speedUnit: string;
}) => {
  const { surveyStore } = useStores();

  const roadSpeed =
    averageRoadSpeed ??
    (type ? surveyStore.getDefaultAverageRoadSpeed(type) : null);

  const aadt =
    averageDailyTraffic ??
    (type ? surveyStore.getDefaultAverageDailyTraffic(type) : null);

  return (
    <HStack
      gap={{ "2xl": 1, xl: 2 }}
      p={2}
      minW={{ "2xl": "120px", xl: "234px" }}
      fontSize="sm"
    >
      <Text color="gray.500">Speed</Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
      >{`${roadSpeed} ${speedUnit}`}</Text>
      •<Text color="gray.500">AADT</Text>
      <Text fontWeight="semibold">{formatNumber(aadt!)}</Text>
    </HStack>
  );
};

const LaneComparisonColumn = ({
  way,
  lane,
  score,
  speedUnit,
  editLabel,
  onEdit,
}: {
  way?: WayLike;
  lane?: Lane;
  score?: React.ComponentProps<typeof HlaScoreView>["score"];
  speedUnit: string;
  editLabel: string;
  onEdit: () => void;
}) => (
  <Stack h="100%" w="100%" minH="300px" gap={4}>
    {lane && way && (
      <Stack align="center" gap={4} minW={0}>
        <Box minW={0} overflow="hidden" w="100%">
          <CrossSectionView
            lit={!!way.lit}
            lane={lane}
            canEdit={false}
            readOnly
            isSelected={false}
          />
        </Box>

        <WayStats
          averageRoadSpeed={way.averageRoadSpeed}
          averageDailyTraffic={way.averageDailyTraffic}
          type={way.type}
          speedUnit={speedUnit}
        />
      </Stack>
    )}

    <Box>
      <HlaScoreView score={score} />
    </Box>

    <Button
      variant="outline"
      colorPalette="primary"
      size="sm"
      fontSize="sm"
      alignSelf="center"
      onClick={onEdit}
    >
      {editLabel}
    </Button>
  </Stack>
);

const ChangeCard = ({
  change,
  speedUnit,
}: {
  change: AttributeChange;
  speedUnit: string;
}) => (
  <Table.Row>
    <Table.Cell>
      <Text fontWeight="semibold">{change.label}</Text>
    </Table.Cell>

    <Table.Cell>
      <Text whiteSpace="pre-line">
        {formatChangeValue(
          change.key,
          change.hlaValue,
          change.simulationValue,
          speedUnit,
        )}
      </Text>
    </Table.Cell>

    <Table.Cell>
      <Text whiteSpace="pre-line">
        {formatChangeValue(
          change.key,
          change.simulationValue,
          change.hlaValue,
          speedUnit,
        )}
      </Text>
    </Table.Cell>
  </Table.Row>
);

const WhatChanged = ({
  changes,
  speedUnit,
}: {
  changes: AttributeChange[];
  speedUnit: string;
}) => (
  <Card.Root alignSelf={"center"}>
    <Stack bg="white" py={4} gap={2} align={"center"}>
      <Text fontWeight="bold" flexShrink={0}>
        What changed
      </Text>

      {changes.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No attribute changes detected.
        </Text>
      ) : (
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Attribute</Table.ColumnHeader>
              <Table.ColumnHeader>Before</Table.ColumnHeader>
              <Table.ColumnHeader>After</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {changes.map((change) => (
              <ChangeCard
                key={change.key}
                change={change}
                speedUnit={speedUnit}
              />
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Stack>
  </Card.Root>
);

const HlaSimulationComparisonViewBase = ({
  distanceUnit,
}: {
  distanceUnit: DistanceUnit;
}) => {
  const { hlaStore, simulationStore, mapStore } = useStores();

  const speedUnit = getSpeedUnit(distanceUnit);

  const hlaWay = hlaStore.getSelected();
  const hlaLane = hlaStore.getSelectedLane();

  const simulationWay = simulationStore.selectedSimulationWay;
  const simulationLane = simulationStore.getSelectedLane();

  const changes = [
    ...getWayChanges(hlaWay, simulationWay),
    ...getLaneChanges(hlaLane, simulationLane),
  ];

  const afterPhotoUrl = simulationWay?.photo?.url;

  return (
    <Stack gap={{ "2xl": 6, xl: 3 }} py={4} px={6} w="full">
      <Text fontSize="xl" fontWeight="bold" flexShrink={0}>
        Comparison view
      </Text>

      <HStack gap={{ "2xl": 6, xl: 3 }} alignItems={"stretch"}>
        <ComparisonCard
          title="Before"
          photo={
            <ComparisonPhoto
              photoUrl={null}
              alt="HLA reference"
              streetViewMapSource={mapStore.streetViewMapSource}
            />
          }
        >
          <LaneComparisonColumn
            way={hlaWay}
            lane={hlaLane}
            score={hlaLane?.score!!}
            speedUnit={speedUnit}
            editLabel="Edit HLA"
            onEdit={() => hlaStore.editHla()}
          />
        </ComparisonCard>

        <ComparisonCard
          title="After"
          photo={
            <ComparisonPhoto
              photoUrl={afterPhotoUrl}
              alt="Simulation reference"
              streetViewMapSource={mapStore.streetViewMapSource}
            />
          }
        >
          <LaneComparisonColumn
            way={simulationWay}
            lane={simulationLane}
            score={simulationLane?.score!!}
            speedUnit={speedUnit}
            editLabel="Edit simulation"
            onEdit={() => simulationStore.editSimulation()}
          />
        </ComparisonCard>
      </HStack>

      <WhatChanged changes={changes} speedUnit={speedUnit} />
    </Stack>
  );
};

export const HlaSimulationComparisonView = observer(
  HlaSimulationComparisonViewBase,
);
